/**
 * CLOV-1993 / CLOV-1642 fix.
 *
 * Wraps a component in an `@emotion/react` `<CacheProvider>` so that BPK 2.0
 * components (Chakra-backed: `BpkProvider`, `BpkBox`, …) can hydrate cleanly
 * under `@skyscanner-internal/local-components-client`'s legacy
 * `ReactDOM.hydrate()`.
 *
 * Why we need an outer `<CacheProvider>` at all:
 * Without one, Chakra's *inner* `<CacheProvider>` is the first Emotion cache
 * boundary in the tree, and its `useInsertionEffect` runs after React has
 * already compared SSR'd `className`s during hydrate. React detects a
 * mismatch and legacy `hydrate()` silently bails the whole subtree. Adding an
 * outer `<CacheProvider>` gives Emotion a chance to initialise its hydration
 * state before `BpkProvider` mounts.
 *
 * Why per-instance cache (NOT a module-level singleton):
 * `local-components-server` reuses the SSR bundle across requests. A
 * singleton cache would work on the first request and silently drop inline
 * styles on every request after that, breaking SSR for everyone else.
 * `useState` runs its initialiser once per render — on the server that means
 * once per request, so each request gets its own clean cache. On the client,
 * each `hydrateLocalComponents` call mounts the wrapper fresh; independent
 * per-partial caches converge on the same registered class IDs because
 * `@emotion/cache` auto-discovers existing `<style data-emotion="bpk …">`
 * tags already in the document.
 *
 * Same mechanism as carhire-homepage's fix for CLOV-1642
 * (https://github.com/Skyscanner/carhire-homepage/pull/5492).
 */
import { useState, type ComponentType } from 'react';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

const EMOTION_CACHE_KEY = 'bpk';

const createBpkEmotionCache = () => createCache({ key: EMOTION_CACHE_KEY });

const withEmotionCache = <P extends object>(
  Component: ComponentType<P>,
): ComponentType<P> => {
  const Wrapped = (props: P) => {
    const [cache] = useState(createBpkEmotionCache);
    return (
      <CacheProvider value={cache}>
        <Component {...props} />
      </CacheProvider>
    );
  };
  Wrapped.displayName = `withEmotionCache(${
    Component.displayName ?? Component.name ?? 'Component'
  })`;
  return Wrapped;
};

export default withEmotionCache;
